import Button from "./Button";
import Modal, { Content, ModalFooter, Header } from "../Modal/Modal";
import React, { useRef, useState, useEffect } from "react";
import styles from "./ProviderTagsModal.module.scss";
import { Heading, Text } from "../Typography";
import { CloseIcon as CloseXIcon } from "@netapp/bxp-style/react-icons/Action";
import { FormFieldInputNew, useForm } from "../Forms";
import _ from "lodash";
import { set } from "lodash/fp";

export const AddNewTagButton = ({ resourceLabelSingular, form, isDisabled, disabledAddTooltip, max, dataTestId }) => {
    const { handleFormChange, formState } = form;
    return <>
        <Button tooltipLeft={true} data-testid={dataTestId && `${dataTestId}-button`} tooltip={isDisabled ? disabledAddTooltip : null} disabled={isDisabled} type="text"
            onClick={() => {
                handleFormChange((formState.tags || []).concat([{ key: "", value: "" }]), { name: "tags" })
            }}>Add new {resourceLabelSingular}</Button>
        {max && <Text data-testid={dataTestId && `${dataTestId}-maximum-tags-text`} style={{ marginLeft: 8 }}>(Maximum of {max} tags)</Text>}
    </>
}

export const ProviderTagsGridHeader = ({ resourceLabelPlural, keyName, valueName, dataTestId }) => {
    const valueHeader = resourceLabelPlural ? `${resourceLabelPlural} value` : valueName;
    const keyHeader = resourceLabelPlural ? `${resourceLabelPlural} key` : keyName;
    return <div data-testid={dataTestId && `${dataTestId}-tags-key-value-row`} className={styles['tags-grid-header']}>
        <Heading data-testid={dataTestId && `${dataTestId}-tags-key-header`} level={4} underline>{keyHeader} </Heading>
        <Heading data-testid={dataTestId && `${dataTestId}-tags-value-header`} level={4} underline>{valueHeader}</Heading>
    </div>
}

export const ProviderTagsGrid = ({ form, keyPlaceholder, valuePlaceholder, dataTestId }) => {
    const { formState, formErrors, handleFormChange, submissionAttempted } = form;
    const { tags } = formState;
    const dirty = useRef({})

    return <div className={styles['tags-grid']}>
        {_.reduceRight(tags.length > 0 ? tags : [{ key: "", value: "" }], (accumulator, tag, index) => {
            const keyInError = formErrors[`tags[${index}].key`];
            const valueInError = formErrors[`tags[${index}].value`];
            const keyDirty = dirty.current[`[${index}].key`];
            const valueDirty = dirty.current[`[${index}].value`];

            return accumulator.concat(<React.Fragment key={index}>
                <FormFieldInputNew
                    form={form}
                    onChange={(e) => {
                        dirty.current[`[${index}].key`] = true;
                        handleFormChange(set(`[${index}].key`, e.target.value)(tags), { name: "tags" })
                    }}
                    value={tag.key}
                    showError={keyInError && (submissionAttempted || keyDirty)}
                    errorMessage={formErrors[`tags[${index}].key`]}
                    placeholder={keyPlaceholder}
                    style={{ marginBottom: 0 }}
                    dataTestId={dataTestId && `${dataTestId}-tag-key-${tag.key}`}
                />
                <FormFieldInputNew
                    form={form}
                    onChange={(e) => {
                        dirty.current[`[${index}].value`] = true;
                        handleFormChange(set(`[${index}].value`, e.target.value)(tags), { name: "tags" })
                    }}
                    value={tag.value}
                    showError={valueInError && (submissionAttempted || valueDirty)}
                    errorMessage={formErrors[`tags[${index}].value`]}
                    placeholder={valuePlaceholder}
                    style={{ marginBottom: 0 }}
                    dataTestId={dataTestId && `${dataTestId}-tag-value-${tag.value}`}
                />
                <Button data-testid={'remove-tag-button'} variant="icon" color="secondary" style={{ width: 24, height: 24 }} onClick={() => {
                    handleFormChange([...tags.slice(0, index), ...tags.slice(index + 1)], { name: "tags" })
                }}><CloseXIcon /></Button>
            </React.Fragment>)
        }, [])}
    </div>
}

export const ProviderTagsWidget = React.memo(({
    resourceLabelSingular = "tag",
    resourceLabelPlural = "tags",
    onChange,
    keyPlaceholder,
    valuePlaceholder,
    max,
    maxLabel,
    disabledAddTooltip,
    validator,
    defaultValue = [],
    form: _form,
    dataTestId
}) => {
    const createdForm = useForm({ tags: defaultValue }, validator);
    const form = _form || createdForm

    useEffect(() => {
        if (onChange) {
            onChange(form.formState.tags)
        }
    }, [form])
    return <>
        <div className={styles['widget-row']}>
            <AddNewTagButton dataTestId={dataTestId && `${dataTestId}-add-new-tag`} resourceLabelSingular={resourceLabelSingular} disabledAddTooltip={disabledAddTooltip}
                isDisabled={max && (form.formState.tags.length >= max)} form={form} max={max} />
        </div>
        {(maxLabel || max) && <Text style={{ marginBottom: 24 }}>You can add up to {maxLabel || max} {resourceLabelPlural}.</Text>}
        <ProviderTagsGridHeader dataTestId={dataTestId && `${dataTestId}-grid-header`} resourceLabelSingular={resourceLabelSingular} />
        <ProviderTagsGrid dataTestId={dataTestId && `${dataTestId}-grid`} form={form} keyPlaceholder={keyPlaceholder} valuePlaceholder={valuePlaceholder} />
    </>
});

const tagsDefault = [{ key: "", value: "" }];

const ProviderTagsModal = React.memo(({
    resourceLabelSingular = "tag",
    resourceLabelPlural = "tags",
    modalHeader,
    closeTrigger,
    onSave,
    keyPlaceholder,
    valuePlaceholder,
    max,
    disabledAddTooltip,
    validator,
    fixedHeight,
    defaultValue = tagsDefault,
    dataTestId
}) => {

    const [isSubmitting, setIsSubmitting] = useState(false);
    const form = useForm({ tags: defaultValue }, validator);
    const { handleFormSubmit } = form;
    return <Modal className={styles.modal} style={fixedHeight ? { maxHeight: fixedHeight } : undefined}>
        <Header>{modalHeader ? modalHeader : `Add ${resourceLabelPlural}`}</Header>
        <Content>
            <div className={styles['widget-row']}>
                <AddNewTagButton dataTestId={dataTestId && `${dataTestId}-add-new-tag`} resourceLabelSingular={resourceLabelSingular} disabledAddTooltip={disabledAddTooltip}
                    isDisabled={max && (form.formState.tags.length >= max)} form={form} max={max} />
            </div>
            <ProviderTagsGridHeader dataTestId={dataTestId && `${dataTestId}-grid-header`} resourceLabelPlural={resourceLabelPlural} />
            <ProviderTagsGrid dataTestId={dataTestId && `${dataTestId}-grid`} form={form} keyPlaceholder={keyPlaceholder} valuePlaceholder={valuePlaceholder} />
        </Content>
        <ModalFooter>
            <Button isSubmitting={isSubmitting} color="primary" onClick={handleFormSubmit(async () => {
                setIsSubmitting(true);
                let input = form.formState.tags;
                const returnValue = onSave(input) || (Promise.resolve());
                try {
                    await returnValue;
                    closeTrigger();
                } catch (e) {
                    setIsSubmitting(false);
                }
            })}>Save</Button>
            <Button color="secondary" onClick={closeTrigger}>Cancel</Button>
        </ModalFooter>
    </Modal>
});

export const ViewProviderTagsModal = React.memo(({
    resourceLabelPlural = "Tags",
    closeTrigger,
    tags,
    SubLabel,
    fixedHeight
}) => {
    return <Modal className={styles.modal} style={fixedHeight ? { maxHeight: fixedHeight } : undefined}>
        <Header>View {resourceLabelPlural}</Header>
        <Content>
            {SubLabel && <div className={styles['sublabel-row']}><SubLabel /></div>}
            <ProviderTagsGridHeader resourceLabelPlural={resourceLabelPlural} />
            <div className={styles['tags-grid']}>
                {_.map(tags, (tag, index) => {
                    return <React.Fragment key={index}>
                        <FormFieldInputNew
                            value={tag.key}
                            disabled={true}
                        />
                        <FormFieldInputNew
                            value={tag.value}
                            disabled={true}
                        />
                        <div />
                    </React.Fragment>
                })}
            </div>
        </Content>
        <ModalFooter>
            <Button color="primary" onClick={closeTrigger}>Close</Button>
        </ModalFooter>
    </Modal>
});

export default ProviderTagsModal;
